/* eslint-disable @typescript-eslint/no-explicit-any */
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiService, DefaultHttpHeaders, HeadersEnum } from '../api.service';
import { Injectable } from '@angular/core';
import { delay, finalize, take } from 'rxjs/operators';
import { Mocks } from '@core/services/mocks/mocks';
import { LicensePlate } from '@core/models/swagger';
import { select } from '@ngrx/store';
import { getInitFlow } from '@store/flow-data/init-flow';
import { InitFlow } from '@core/models/swagger/initFlow';

@Injectable({
  providedIn: 'root',
})
export class ValidationService extends ApiService {
  mocked = false;

  private readonly loadingSubjects: Record<string, BehaviorSubject<boolean>> = {
    postPostCode: new BehaviorSubject<boolean>(false),
    postEmail: new BehaviorSubject<boolean>(false),
    postPromotionCode: new BehaviorSubject<boolean>(false),
    postIban: new BehaviorSubject<boolean>(false),
    postLicensePlate: new BehaviorSubject<boolean>(false),
  };

  readonly postPostCodeLoading$ = this.loadingSubjects.postPostCode.asObservable();
  readonly postEmailLoading$ = this.loadingSubjects.postEmail.asObservable();
  readonly postPromotionCodeLoading$ = this.loadingSubjects.postPromotionCode.asObservable();
  readonly postIban$ = this.loadingSubjects.postIban.asObservable();
  readonly postLicensePlate$ = this.loadingSubjects.postLicensePlate.asObservable();

  private executePostRequest<TRequest, TResponse>({
    endpoint,
    body,
    requestKey,
    headers
  }: {
    endpoint: string;
    body: TRequest;
    requestKey: string;
    headers?: DefaultHttpHeaders;
  }): Observable<TResponse> {
    this.loadingSubjects[requestKey].next(true);
    if (this.mocked) {
      return Mocks[requestKey]().pipe(
        delay(2000),
        finalize(() => this.loadingSubjects[requestKey].next(false))
      );
    }

    this.store.pipe(select(getInitFlow), take(1)).subscribe((initFlow: InitFlow) => {
      headers = {
        ...headers,
        [HeadersEnum.X_CORRELATION_ID]: initFlow?.correlationId?.toString(),
      };
    });

    return this.post<TRequest, TResponse>({ suffix: endpoint, body, headers }).pipe(
      finalize(() => this.loadingSubjects[requestKey].next(false))
    );
  }

  postPostCode(body: PostCodeBody): Observable<PostCodeResponse> {
    return this.executePostRequest<PostCodeBody, PostCodeResponse>({
      endpoint: '/validation/postcode',
      body,
      requestKey: 'postPostCode',
    });
  }

  postLicensePlate(body: LicensePlateBody): Observable<LicensePlateResponse> {
    return this.executePostRequest<LicensePlateBody, LicensePlateResponse>({
      endpoint: '/validation/licenseplate',
      body,
      requestKey: 'postLicensePlate',
    });
  }

  postEmail(body: EmailBody): Observable<EmailResponse> {
    return this.executePostRequest<EmailBody, EmailResponse>({
      endpoint: '/validation/email',
      body,
      requestKey: 'postEmail',
    });
  }

  postPromotionCode(body: PromotionCodeBody): Observable<PromotionCodeResponse> {
    return this.executePostRequest<PromotionCodeBody, PromotionCodeResponse>({
      endpoint: '/validation/promotioncode',
      body,
      requestKey: 'postPromotionCode',
    });
  }

  postIban(body: IbanBody): Observable<PromotionCodeResponse> {
    return this.executePostRequest<IbanBody, IbanResponse>({
      endpoint: '/validation/iban',
      body,
      requestKey: 'postIban',
    });
  }
}

interface PostCodeBody {
  postCode: string;
}
export interface PostCodeResponse {
  isValid: boolean;
}

interface LicensePlateBody {
  licensePlate: LicensePlate;
}
export interface LicensePlateResponse {
  isValid: boolean;
}

interface EmailBody {
  email: string;
}
export interface EmailResponse {
  isValid: boolean;
}

interface PromotionCodeBody {
  cooperationId: string;
  promotionCode: string;
  ratingDate: string;
}
export interface PromotionCodeResponse {
  isValid: boolean;
}

interface IbanBody {
  iban: {
    iban: string;
    ibanType: string;
  };
}
export interface IbanResponse {
  isValid: boolean;
}
